/**
 * Meetings (Aziz, 2026-09-27): every meeting you organise or are invited to, team only
 * or with clinics, and the script calls. New meeting opens the planner.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CalendarPlus, MapPin, Users, Video } from 'lucide-react';
import { Card, PageHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import { fetchMeetings, MEETING_STATUS, type Meeting } from '@/lib/calls';
import NewMeetingDialog from '@/components/meetings/NewMeetingDialog';
import { GENERAL_STATUS } from '@/components/meetings/MeetingDetail';

type Tab = 'upcoming' | 'waiting' | 'outcomes' | 'past';
const TABS: Array<[Tab, string]> = [['upcoming', 'Upcoming'], ['waiting', 'Waiting for the clinic'], ['outcomes', 'Record outcomes'], ['past', 'Past']];

function tabOf(m: Meeting): Tab {
  if (['link_sent', 'booking'].includes(m.status)) return 'waiting';
  if (['review', 'awaiting_audio', 'transcribing'].includes(m.status)) return 'outcomes';
  if (m.status === 'booked' && m.ends_at && new Date(m.ends_at).getTime() > Date.now()) return 'upcoming';
  if (m.status === 'booked') return 'outcomes';
  return 'past';
}

const when = (iso?: string | null) => iso ? new Date(iso).toLocaleString('en-GB', { timeZone: 'Asia/Dubai', weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Time not picked yet';

export default function MeetingsPage() {
  const [tab, setTab] = useState<Tab>('upcoming');
  const [open, setOpen] = useState(false);
  const q = useQuery({ queryKey: ['meetings', 'all'], queryFn: () => fetchMeetings({}) });
  const groups = useMemo(() => {
    const g: Record<Tab, Meeting[]> = { upcoming: [], waiting: [], outcomes: [], past: [] };
    for (const m of q.data ?? []) g[tabOf(m)].push(m);
    g.upcoming.sort((a, b) => (a.starts_at ?? '').localeCompare(b.starts_at ?? ''));
    return g;
  }, [q.data]);
  const rows = groups[tab];

  return (
    <div>
      <PageHeader title="Meetings" subtitle="Team meetings and meetings with clinics. Only times when everyone is free are offered."
        actions={<Button icon={<CalendarPlus size={14} />} onClick={() => setOpen(true)} data-testid="new-meeting">New meeting</Button>} />
      <div className="flex flex-wrap gap-2 mb-4" role="tablist">
        {TABS.map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={cn('h-8 px-3 rounded-full text-xs border', tab === k ? 'border-accent-500 text-accent-300 bg-accent-500/10' : 'border-slate-700 text-muted hover:text-muted-strong')}>
            {label}{groups[k].length ? ` (${groups[k].length})` : ''}
          </button>
        ))}
      </div>
      {q.isLoading && <p className="text-sm text-muted">Loading...</p>}
      {!q.isLoading && rows.length === 0 && (
        <Card><p className="text-sm text-muted">{tab === 'upcoming' ? 'Nothing coming up. Press New meeting to book one.' : 'Nothing here.'}</p></Card>
      )}
      <div className="space-y-2">
        {rows.map((m) => {
          const general = m.purpose === 'meeting';
          const st = general ? (GENERAL_STATUS[m.status] ?? MEETING_STATUS[m.status]) : MEETING_STATUS[m.status];
          const people = 1 + (m.attendees?.length ?? 0) + m.invitees.length;
          return (
            <Link key={m.id} to={`/meetings/${m.id}`} data-testid="meeting-row"
              className="block rounded-card border border-slate-800 bg-bg-card/85 p-4 hover:border-accent-500/60 transition">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-muted-strong">{m.title}</span>
                <span className={cn('px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide', st?.cls)}>{st?.label ?? m.status}</span>
                <span className="text-[10px] uppercase tracking-wide text-muted">{general ? (m.kind === 'client' ? 'with a clinic' : 'team') : 'script call'}</span>
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs text-muted">
                <span className="tabular-nums">{when(m.starts_at)}</span>
                <span className="inline-flex items-center gap-1"><Users size={12} /> {people}</span>
                {general && (m.with_meet === false && m.location ? <span className="inline-flex items-center gap-1"><MapPin size={12} /> {m.location}</span>
                  : <span className="inline-flex items-center gap-1"><Video size={12} /> Google Meet</span>)}
                {m.client_name && <span>{m.client_name}</span>}
              </div>
            </Link>
          );
        })}
      </div>
      {open && <NewMeetingDialog open onClose={() => setOpen(false)} />}
    </div>
  );
}
